// src/App.jsx — Updated with all new screens wired in
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login          from "./pages/Login";
import EntitySetup    from "./pages/EntitySetup";
import BillingUpload  from "./pages/BillingUpload";
import ExpenseEntry   from "./pages/ExpenseEntry";
import Dashboard      from "./pages/Dashboard";
import JournalEntries from "./pages/JournalEntries";
import Layout         from "./components/shared/Layout";
import { api }        from "./services/api";
import ExportScreen   from "./pages/ExportAndDashboards";
import { TPAAging, AdminDashboard } from "./pages/ExportAndDashboards";
import ColumnMapping  from "./pages/AdminScreens";
import {
  ZohoSetup, LedgerCreate, FixedAssets, UserManagement,
  ManualJournalEntry, StatutoryPayments, SalaryJournal, TPASettlement
} from "./pages/AdminScreens";

export default function App() {
  const [user,     setUser]     = useState(null);
  const [entity,   setEntity]   = useState(null);
  const [entities, setEntities] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    const u = localStorage.getItem("ts360_user");
    const t = localStorage.getItem("ts360_token");
    const e = localStorage.getItem("ts360_entity");
    if (u && t) {
      const user = JSON.parse(u);
      setUser(user);
      if (e) setEntity(JSON.parse(e));
      fetchEntities(user);
    } else {
      setLoading(false);
    }
  }, []);

  async function fetchEntities(u) {
    try {
      if (u.role === "iicipl_admin") {
        const d = await api.get("/entities");
        const nextEntities = d.entities || [];
        setEntities(nextEntities);
        const storedEntity = localStorage.getItem("ts360_entity");
        if (storedEntity) {
          const stored = JSON.parse(storedEntity);
          const fresh = nextEntities.find(item => item._id === stored._id);
          if (fresh) { setEntity(fresh); localStorage.setItem("ts360_entity", JSON.stringify(fresh)); }
        }
      } else {
        const ids = u.entityAccess || [];
        if (ids.length === 1) {
          const d = await api.get(`/entities/${ids[0]}`);
          setEntity(d.entity);
          localStorage.setItem("ts360_entity", JSON.stringify(d.entity));
        } else {
          const res = await Promise.all(ids.map(id => api.get(`/entities/${id}`)));
          setEntities(res.map(r => r.entity));
        }
      }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  function handleLogin(u) { setUser(u); fetchEntities(u); }
  function handleLogout() { localStorage.clear(); setUser(null); setEntity(null); setEntities([]); }
  function selectEntity(e) { setEntity(e); localStorage.setItem("ts360_entity", JSON.stringify(e)); }
  function handleEntityCreated(e) {
    setEntity(e);
    setEntities(prev => [e, ...prev.filter(item => item._id !== e._id)]);
    localStorage.setItem("ts360_entity", JSON.stringify(e));
  }
  function handleEntityUpdated(e) {
    setEntity(current => current?._id === e._id || !current ? e : current);
    setEntities(prev => prev.map(item => item._id === e._id ? e : item));
    localStorage.setItem("ts360_entity", JSON.stringify(e));
  }

  if (loading) return <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#1B3F6E",color:"#AED6F1",fontFamily:"Inter,system-ui,sans-serif"}}>Loading…</div>;
  if (!user)   return <Login onLogin={handleLogin} />;
  if (!entity && entities.length > 1) return <EntitySelector entities={entities} onSelect={selectEntity} user={user} onLogout={handleLogout} />;

  const active = entity || entities[0];

  return (
    <BrowserRouter>
      <Layout user={user} entity={active} onLogout={handleLogout}>
        <Routes>
          <Route path="/" element={<Navigate to={user.role==="hospital_staff"?"/upload":user.role==="iicipl_admin"?"/admin":"/dashboard"} replace />} />

          {/* All roles */}
          <Route path="/upload"    element={<BillingUpload  entity={active} />} />
          <Route path="/expenses"  element={<ExpenseEntry   entity={active} />} />
          <Route path="/dashboard" element={<Dashboard      entity={active} />} />

          {/* Role 2+ */}
          <Route path="/entries"   element={<JournalEntries entity={active} user={user} />} />
          <Route path="/export"    element={<ExportScreen   entity={active} />} />
          <Route path="/tpa"       element={<TPAAging       entity={active} />} />
          <Route path="/assets"    element={<FixedAssets    entity={active} user={user} />} />

          {/* Admin only */}
          <Route path="/entities"  element={<EntitySetup    onEntityCreated={handleEntityCreated} />} />
          <Route path="/admin"     element={<AdminDashboard entities={entities} />} />
          <Route path="/mapping"   element={<ColumnMapping  entity={active} onEntityUpdated={handleEntityUpdated} />} />
          <Route path="/zoho"      element={<ZohoSetup      entity={active} onEntityUpdated={handleEntityUpdated} />} />
          <Route path="/ledgers"   element={<LedgerCreate   entity={active} />} />
          <Route path="/users"     element={<UserManagement entities={entities} />} />

          {/* New screens */}
          <Route path="/manual-entry"   element={<ManualJournalEntry entity={active} />} />
          <Route path="/statutory"      element={<StatutoryPayments  entity={active} />} />
          <Route path="/salary"         element={<SalaryJournal      entity={active} />} />
          <Route path="/tpa-settle"     element={<TPASettlement      entity={active} />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

function EntitySelector({ entities, onSelect, user, onLogout }) {
  return (
    <div style={{minHeight:"100vh",background:"#f0f4f8",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"Inter,system-ui,sans-serif"}}>
      <div style={{background:"#fff",borderRadius:16,padding:36,maxWidth:480,width:"100%",boxShadow:"0 4px 20px rgba(0,0,0,.1)"}}>
        <div style={{fontSize:20,fontWeight:700,color:"#1B3F6E",marginBottom:6}}>Select entity</div>
        <div style={{fontSize:13,color:"#607D8B",marginBottom:24}}>Welcome, {user.name}. Which entity today?</div>
        {entities.map(e=>(
          <button key={e._id} onClick={()=>onSelect(e)} style={{width:"100%",padding:"14px 18px",marginBottom:10,background:"#f8fafc",border:"1.5px solid #CFD8DC",borderRadius:10,cursor:"pointer",textAlign:"left",display:"flex",flexDirection:"column"}}>
            <div style={{fontSize:14,fontWeight:600,color:"#1B3F6E"}}>{e.name}</div>
            <div style={{fontSize:12,color:"#90A4AE",marginTop:2}}>{e.location} · {e.entityType==="pvt_ltd"?"Pvt Ltd":"Proprietorship"}</div>
          </button>
        ))}
        <button onClick={onLogout} style={{marginTop:8,background:"none",border:"none",color:"#90A4AE",fontSize:12,cursor:"pointer",width:"100%",textAlign:"center"}}>Sign out</button>
      </div>
    </div>
  );
}
